"use client";

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Loader2,
  Plus,
  DollarSign,
  User,
  Tag,
  ArrowDownCircle,
  ArrowUpCircle,
  FileText,
  Sparkles,
  X,
  ChevronDown,
} from "lucide-react";
import { toast } from "react-hot-toast";

export function InlinePromosAddBar({
  onAddTransaction,
  transactionDetails = [],
  currentBalance = 0,
  isLoading = false,
}) {
  const [transactionType, setTransactionType] = useState("withdraw"); // "withdraw" | "deposit"
  const [amount, setAmount] = useState("");
  const [playerName, setPlayerName] = useState("");
  const [promoSearch, setPromoSearch] = useState("");
  const [selectedPromo, setSelectedPromo] = useState("");
  const [isPromoDropdownOpen, setIsPromoDropdownOpen] = useState(false);
  const [highlightedPromoIndex, setHighlightedPromoIndex] = useState(0);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Input refs for automatic focus chaining
  const amountInputRef = useRef(null);
  const playerInputRef = useRef(null);
  const promoInputRef = useRef(null);
  const promoDropdownRef = useRef(null);
  const promoListRef = useRef(null);
  const notesInputRef = useRef(null);

  const isDeposit = transactionType === "deposit";
  const promoOptions = useMemo(() => {
    return Array.isArray(transactionDetails) ? transactionDetails.filter(Boolean) : [];
  }, [transactionDetails]);

  // Filter promotions based on search term
  const filteredPromos = useMemo(() => {
    if (!promoSearch.trim()) return promoOptions;
    const term = promoSearch.trim().toLowerCase();
    return promoOptions.filter((p) => p.toLowerCase().includes(term));
  }, [promoOptions, promoSearch]);

  // Reset highlight index when filtered promotions change
  useEffect(() => {
    setHighlightedPromoIndex(0);
  }, [filteredPromos.length]);

  // Auto-scroll dropdown container so highlighted item is always in view
  useEffect(() => {
    if (isPromoDropdownOpen && promoListRef.current) {
      const container = promoListRef.current;
      const highlightedEl = container.querySelector(
        `[data-index="${highlightedPromoIndex}"]`
      );
      if (highlightedEl) {
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elTop = highlightedEl.offsetTop;
        const elBottom = elTop + highlightedEl.offsetHeight;

        if (elTop < containerTop) {
          container.scrollTop = elTop;
        } else if (elBottom > containerBottom) {
          container.scrollTop = elBottom - container.clientHeight;
        }
      }
    }
  }, [highlightedPromoIndex, isPromoDropdownOpen]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (promoDropdownRef.current && !promoDropdownRef.current.contains(e.target)) {
        setIsPromoDropdownOpen(false);
        setPromoSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const selectPromo = useCallback((promo) => {
    setSelectedPromo(promo);
    setNotes(promo);
    setPromoSearch("");
    setIsPromoDropdownOpen(false);
    // Smoothly focus Notes input for any quick adjustments
    setTimeout(() => {
      notesInputRef.current?.focus();
    }, 50);
  }, []);

  const handleTypeChange = (type) => {
    setTransactionType(type);
    if (type === "deposit") {
      setPlayerName("");
      setSelectedPromo("");
      setPromoSearch("");
      setNotes("");
      setIsPromoDropdownOpen(false);
    }
    setTimeout(() => {
      amountInputRef.current?.focus();
    }, 50);
  };

  // Keyboard navigation for Amount Input
  const handleAmountKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const parsedAmount = parseFloat(amount);
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }

      if (isDeposit) {
        handleSubmit();
      } else {
        playerInputRef.current?.focus();
      }
    }
  };

  // Keyboard navigation for Player Name Input
  const handlePlayerKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!playerName.trim()) {
        toast.error("Please enter a player name");
        return;
      }
      // Advance to Promo selector and open dropdown
      promoInputRef.current?.focus();
      setIsPromoDropdownOpen(true);
    }
  };

  // Keyboard navigation for Promo Dropdown
  const handlePromoKeyDown = (e) => {
    if (isPromoDropdownOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (filteredPromos.length > 0) {
          setHighlightedPromoIndex((prev) => (prev + 1) % filteredPromos.length);
        }
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (filteredPromos.length > 0) {
          setHighlightedPromoIndex((prev) =>
            prev <= 0 ? filteredPromos.length - 1 : prev - 1
          );
        }
        return;
      }
      if (e.key === "PageDown") {
        e.preventDefault();
        if (filteredPromos.length > 0) {
          setHighlightedPromoIndex((prev) =>
            Math.min(filteredPromos.length - 1, prev + 5)
          );
        }
        return;
      }
      if (e.key === "PageUp") {
        e.preventDefault();
        if (filteredPromos.length > 0) {
          setHighlightedPromoIndex((prev) => Math.max(0, prev - 5));
        }
        return;
      }
      if (e.key === "Home") {
        e.preventDefault();
        if (filteredPromos.length > 0) {
          setHighlightedPromoIndex(0);
        }
        return;
      }
      if (e.key === "End") {
        e.preventDefault();
        if (filteredPromos.length > 0) {
          setHighlightedPromoIndex(filteredPromos.length - 1);
        }
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        if (filteredPromos.length > 0) {
          e.preventDefault();
          const selected = filteredPromos[highlightedPromoIndex] || filteredPromos[0];
          selectPromo(selected);
          return;
        } else if (promoSearch.trim()) {
          // If free text was entered that isn't in presets, treat as notes
          e.preventDefault();
          setNotes(promoSearch.trim());
          setSelectedPromo(promoSearch.trim());
          setIsPromoDropdownOpen(false);
          setPromoSearch("");
          notesInputRef.current?.focus();
          return;
        }
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setIsPromoDropdownOpen(false);
        setPromoSearch("");
        return;
      }
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (selectedPromo) {
        notesInputRef.current?.focus();
      } else {
        setIsPromoDropdownOpen(true);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsPromoDropdownOpen(true);
    }
  };

  // Keyboard navigation for Notes Input
  const handleNotesKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!amount) {
      toast.error("Please enter an amount");
      amountInputRef.current?.focus();
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error("Please enter a valid amount");
      amountInputRef.current?.focus();
      return;
    }

    if (!isDeposit) {
      if (!playerName.trim()) {
        toast.error("Please enter a player name");
        playerInputRef.current?.focus();
        return;
      }
      if (!notes.trim()) {
        toast.error("Please select a promotion or enter details");
        notesInputRef.current?.focus();
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        transactionType,
        amount: parsedAmount,
        playerName: isDeposit ? "" : playerName.trim(),
        notes: isDeposit ? "" : notes.trim(),
      };

      const success = await onAddTransaction(payload);
      if (success) {
        // Reset form fields
        setAmount("");
        setPlayerName("");
        setSelectedPromo("");
        setPromoSearch("");
        setNotes("");
        setIsPromoDropdownOpen(false);

        // Return focus to amount ready for immediate next entry
        setTimeout(() => {
          amountInputRef.current?.focus();
        }, 80);
      }
    } catch (error) {
      console.error("Inline promo submit error:", error);
      toast.error(error.message || "Failed to add transaction");
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || isLoading;
  const isFormValid =
    Boolean(amount) &&
    parseFloat(amount) > 0 &&
    (isDeposit || (Boolean(playerName.trim()) && Boolean(notes.trim())));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 mb-8 transition-all">
      {/* Header bar: Title, Type Switcher & Live Balance */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500 animate-pulse" />
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
            Add New Transaction
          </h2>
          <span className="hidden md:flex items-center gap-1 text-[11px] text-gray-400 ml-2">
            <span>Press</span>
            <kbd className="px-1.5 py-0.5 rounded bg-gray-100 border border-gray-300 text-[10px] font-mono text-gray-700">
              Enter ↵
            </kbd>
            <span>to navigate / save</span>
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Segmented Type Toggle */}
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 text-xs font-medium">
            <button
              type="button"
              onClick={() => handleTypeChange("withdraw")}
              disabled={busy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
                !isDeposit
                  ? "bg-rose-500 text-white font-semibold shadow-xs"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <ArrowDownCircle size={14} />
              <span>Withdrawal</span>
            </button>
            <button
              type="button"
              onClick={() => handleTypeChange("deposit")}
              disabled={busy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
                isDeposit
                  ? "bg-emerald-600 text-white font-semibold shadow-xs"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <ArrowUpCircle size={14} />
              <span>Deposit</span>
            </button>
          </div>

          {/* Balance Pill */}
          <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold">
            <span className="text-[10px] text-blue-500 uppercase tracking-wider">
              Pool
            </span>
            <span>
              ${Number(currentBalance || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Form Fields */}
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col lg:flex-row items-stretch lg:items-end gap-3">
          {/* 1. Amount */}
          <div className="w-full lg:w-40 shrink-0">
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <DollarSign size={15} />
              </div>
              <Input
                ref={amountInputRef}
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={handleAmountKeyDown}
                disabled={busy}
                className="pl-8 text-sm font-semibold h-10"
              />
            </div>
          </div>

          {/* Conditional inputs for Withdrawal vs Deposit */}
          {!isDeposit ? (
            <>
              {/* 2. Player Name */}
              <div className="w-full lg:w-48 shrink-0">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Player Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <User size={15} />
                  </div>
                  <Input
                    ref={playerInputRef}
                    type="text"
                    placeholder="Enter player name"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    onKeyDown={handlePlayerKeyDown}
                    disabled={busy}
                    className="pl-8 text-sm h-10"
                  />
                </div>
              </div>

              {/* 3. Promo Searchable Dropdown with Arrow Key Nav */}
              <div className="w-full lg:w-56 shrink-0 relative" ref={promoDropdownRef}>
                <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <Tag size={12} className="text-gray-400" />
                    <span>Promotion</span>
                    <span className="text-red-500">*</span>
                  </span>
                  <span className="text-[10px] text-gray-400 font-normal">
                    (↑/↓ to navigate)
                  </span>
                </label>

                <div className="relative">
                  <Input
                    ref={promoInputRef}
                    type="text"
                    value={isPromoDropdownOpen ? promoSearch : selectedPromo}
                    placeholder="Select or search promo..."
                    onFocus={() => {
                      setPromoSearch(selectedPromo);
                      setIsPromoDropdownOpen(true);
                    }}
                    onChange={(e) => {
                      setPromoSearch(e.target.value);
                      setIsPromoDropdownOpen(true);
                    }}
                    onKeyDown={handlePromoKeyDown}
                    disabled={busy}
                    className="pr-8 text-xs h-10"
                  />
                  <div className="absolute inset-y-0 right-0 pr-2.5 flex items-center gap-1">
                    {selectedPromo ? (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPromo("");
                          setNotes("");
                          setPromoSearch("");
                          promoInputRef.current?.focus();
                        }}
                        className="text-gray-400 hover:text-gray-600 p-0.5"
                      >
                        <X size={13} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => setIsPromoDropdownOpen((prev) => !prev)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                </div>

                {/* Autocomplete Dropdown with Highlight & Scroll */}
                {isPromoDropdownOpen && (
                  <div
                    ref={promoListRef}
                    className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-xl border border-gray-200 max-h-56 overflow-y-auto py-1"
                  >
                    {filteredPromos.length > 0 ? (
                      filteredPromos.map((promo, index) => {
                        const isHighlighted = index === highlightedPromoIndex;
                        const isSelected = selectedPromo === promo;

                        return (
                          <button
                            key={promo}
                            data-index={index}
                            type="button"
                            onClick={() => selectPromo(promo)}
                            onMouseEnter={() => setHighlightedPromoIndex(index)}
                            className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors ${
                              isHighlighted
                                ? "bg-gray-100 text-gray-900 font-semibold"
                                : isSelected
                                ? "bg-yellow-50 text-yellow-900 font-medium"
                                : "text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            <span className="truncate">{promo}</span>
                            {isSelected && (
                              <span className="text-[10px] text-yellow-600 bg-yellow-100 px-1.5 py-0.5 rounded font-semibold shrink-0">
                                Selected
                              </span>
                            )}
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-3 py-2 text-xs text-gray-400 text-center">
                        No matching promotions found
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 4. Promotion Details / Notes */}
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                  <FileText size={12} className="text-gray-400" />
                  <span>Promotion Details</span>
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  ref={notesInputRef}
                  type="text"
                  placeholder="Details (e.g. High Hand winner)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onKeyDown={handleNotesKeyDown}
                  disabled={busy}
                  className="text-sm h-10"
                />
              </div>
            </>
          ) : (
            /* Deposit Mode informational view */
            <div className="flex-1 flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50/70 border border-emerald-100 text-xs text-emerald-800">
              <Sparkles size={16} className="text-emerald-600 shrink-0" />
              <span>
                Deposit will directly credit the promo balance. Player name and promotion details are omitted for pool deposits.
              </span>
            </div>
          )}

          {/* 5. Submit Button */}
          <div className="shrink-0 pt-1 lg:pt-0">
            <Button
              type="submit"
              disabled={busy || !isFormValid}
              className={`w-full lg:w-auto h-10 px-5 font-semibold text-xs uppercase tracking-wider transition-all shadow-xs ${
                isDeposit
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-yellow-400 hover:bg-yellow-500 text-black"
              }`}
            >
              {busy ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <Plus className="mr-1.5 h-4 w-4 stroke-[2.5]" />
                  <span>{isDeposit ? "Add Deposit" : "Add Withdrawal"}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
